import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, Timestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { db, storage } from '../../../firebase';
import { useAdminAuth } from '../../../hooks/admin/useAdminAuth';
import { ref, getDownloadURL, listAll } from 'firebase/storage';
import { format, parse, isBefore } from 'date-fns';
import { calculateFinalPrice, calculateFinalPriceWithVAT, findDiscountByMemberType } from '../../../utils/pricing';
import { useToast } from '../../../hooks/useToast';
import ReservationAttachmentFields from '../../../components/admin/facility-reservations/ReservationAttachmentFields';
import { emptyAttachmentChanges, ReservationAttachments, saveReservationWithAttachments } from '../../../utils/reservationAttachments';
import SmsService from '../../../services/SmsService';

// 상수 정의
const USER_LEVEL_TEXTS = {
  1: '최고 관리자',
  2: '일반 관리자',
  3: '공사 관계자',
  4: '센터 관계자',
  5: '입주업체',
  6: '입주협력업체',
  7: '공공기관',
  8: '항만근로자',
  9: '일반 사용자'
} as const;

const STATUS_COLORS = {
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800',
  pending: 'bg-yellow-100 text-yellow-800'
} as const;

const STATUS_TEXTS = {
  approved: '승인됨',
  rejected: '거절됨',
  cancelled: '취소됨',
  pending: '대기중'
} as const;

// 타입 정의
type ReservationStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';
type ReservationType = 'time-slot' | 'start-end';

interface Reservation extends ReservationAttachments {
  id: string;
  facilityId: string;
  facilityName: string;
  uid: string;
  name: string;
  email: string;
  phone?: string;
  phoneNumber?: string;
  organization?: string;
  reservationDate: Date;
  startTime: number;
  endTime: number;
  reservationType: string;
  price?: number;
  status: ReservationStatus;
  adminComment?: string;
  fileUrl?: string | null;
  applicationFormUrl?: string | null;
  applicationFormFileName?: string | null;
  rentalTermsAgreed?: boolean;
  rentalTermsAgreedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  userLevel?: number;
  discountType?: string;
}

interface Facility {
  id: string;
  name: string;
  reservationType: ReservationType;
  availableTimeSlots?: number[];
  operatingHours?: {
    start: number;
    end: number;
  };
  reservationTimeUnit: number;
  price: number;
  basePrice?: number;
  maxPrice?: number;
  discounts?: Array<{
    id: string;
    memberType: string | number;
    discountRate: number;
  }>;
}

interface EditFormState {
  reservationDate: string;
  startTime: number;
  endTime: number;
  name?: string;
  email?: string;
  phone?: string;
  organization?: string;
}

interface ExistingReservation {
  id: string;
  startTime: number;
  endTime: number;
}

interface DiscountInfo {
  type: 'manual' | 'auto';
  memberType: string;
  discountRate: number;
  description: string;
}

const FacilityReservationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [reservation, setReservation] = useState<Reservation | null>(null);
  const [facility, setFacility] = useState<Facility | null>(null);
  const [facilities, setFacilities] = useState<Facility[]>([]); // 시설물 목록
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [downloadingApplicationForm, setDownloadingApplicationForm] = useState(false);
  const [adminComment, setAdminComment] = useState('');
  const [attachmentUrls, setAttachmentUrls] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { user, userLevel, permissions, loading: authLoading } = useAdminAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  // 관리자가 생성한 예약인지 확인하는 헬퍼 함수
  const isAdminCreated = useCallback((uid: string) => {
    return uid.startsWith('manual_');
  }, []);

  // 페이지 진입 시 스크롤을 최상단으로 이동
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // 수정 모달 관련 상태
  const [attachmentChanges, setAttachmentChanges] = useState(emptyAttachmentChanges);
  const [attachmentOnly, setAttachmentOnly] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditFormState>({
    reservationDate: '',
    startTime: 0,
    endTime: 0,
    name: '',
    email: '',
    phone: '',
    organization: ''
  });
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>(''); // 선택된 시설물 ID
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(null); // 선택된 시설물 정보
  const [existingReservations, setExistingReservations] = useState<ExistingReservation[]>([]);

  // 취소 모달 관련 상태
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // 회원 등급 레벨을 한글 명칭으로 변환 (메모이제이션)
  const getUserLevelText = useCallback((level: number): string => {
    return USER_LEVEL_TEXTS[level as keyof typeof USER_LEVEL_TEXTS] || `레벨 ${level}`;
  }, []);

  // 할인 정보 계산 (메모이제이션)
  const getDiscountInfo = useMemo((): DiscountInfo | null => {
    if (!reservation || !facility) return null;

    // 일반사용자가 감면 사유를 신청한 경우
    if (reservation.userLevel === 9 && reservation.discountType && facility.discounts) {
      const discount = findDiscountByMemberType(facility.discounts, reservation.discountType);
      if (discount) {
        return {
          type: 'manual',
          memberType: reservation.discountType,
          discountRate: discount.discountRate,
          description: `${getUserLevelText(parseInt(reservation.discountType))} 감면 신청`
        };
      }
    }

    // 등업된 사용자의 자동 할인
    if (reservation.userLevel && reservation.userLevel !== 9 && facility.discounts) {
      const userLevel = reservation.userLevel;
      const discount = findDiscountByMemberType(facility.discounts, userLevel);
      if (discount) {
        return {
          type: 'auto',
          memberType: userLevel.toString(),
          discountRate: discount.discountRate,
          description: `${getUserLevelText(userLevel)} 자동 할인`
        };
      }
    }

    return null;
  }, [reservation, facility, getUserLevelText]);

  // 원래 가격 계산 (할인 적용 전) - 메모이제이션
  const getOriginalPrice = useMemo(() => {
    if (!reservation || !facility) return 0;

    // calculateBasePrice를 직접 사용
    const basePrice = calculateFinalPrice(
      {
        price: facility.price,
        basePrice: facility.basePrice,
        maxPrice: facility.maxPrice,
        discounts: []
      },
      {
        reservationType: reservation.reservationType as 'time-slot' | 'start-end',
        startTime: reservation.startTime,
        endTime: reservation.endTime,
        reservationTimeUnit: facility.reservationTimeUnit
      },
      9 // 할인 없는 일반 사용자 기준
    );

    return basePrice;
  }, [reservation, facility]);

  // 예약 정보 가져오기 (메모이제이션)
  const fetchReservation = useCallback(async () => {
    if (!id) return;

    try {
      const docRef = doc(db, 'reservations', id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const reservationData: Reservation = {
          id: docSnap.id,
          facilityId: data.facilityId || '',
          facilityName: data.facilityName || '',
          uid: data.uid || '',
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || data.phoneNumber || '',
          phoneNumber: data.phoneNumber || data.phone || '',
          organization: data.organization || '',
          reservationDate: data.reservationDate?.toDate ? data.reservationDate.toDate() : new Date(),
          startTime: data.startTime || 0,
          endTime: data.endTime || 0,
          reservationType: data.reservationType || '',
          price: data.price || 0,
          status: data.status || 'pending',
          adminComment: data.adminComment || '',
          fileUrl: data.fileUrl || '',
          fileName: data.fileName || '',
          fileStoragePath: data.fileStoragePath || '',
          applicationFormStoragePath: data.applicationFormStoragePath || '',
          applicationFormUrl: data.applicationFormUrl || '',
          applicationFormFileName: data.applicationFormFileName || '',
          rentalTermsAgreed: data.rentalTermsAgreed || false,
          rentalTermsAgreedAt: data.rentalTermsAgreedAt?.toDate ? data.rentalTermsAgreedAt.toDate() : undefined,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(),
          userLevel: data.userLevel || 9,
          discountType: data.discountType || null
        };

        setReservation(reservationData);
        setAdminComment(reservationData.adminComment || '');

        // 첨부파일 URL 설정
        setAttachmentUrls(reservationData.fileUrl ? [reservationData.fileUrl] : []);


        showToast('예약 정보를 성공적으로 불러왔습니다.', { type: 'success' });
      } else {
        const errorMsg = '해당 예약을 찾을 수 없습니다.';
        setError(errorMsg);
        showToast(errorMsg, { type: 'error' });
      }
    } catch (error: unknown) {
      console.error('예약 조회 실패:', error);
      const errorMsg = error instanceof Error ? error.message : '예약 정보를 불러오는 중 오류가 발생했습니다.';
      setError(errorMsg);
      showToast(errorMsg, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id, showToast]);

  const handleDownloadApplicationForm = useCallback(async () => {
    if (!reservation?.applicationFormUrl) return;

    const fallbackFilename = reservation.applicationFormFileName || `application-form-${reservation.id}`;

    setDownloadingApplicationForm(true);
    try {
      const response = await fetch(reservation.applicationFormUrl);
      if (!response.ok) {
        throw new Error(`다운로드 실패 (HTTP ${response.status})`);
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = fallbackFilename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (error: unknown) {
      console.error('대관신청서 다운로드 실패:', error);
      showToast('다운로드에 실패하여 링크로 이동합니다.', { type: 'error' });
      const anchor = document.createElement('a');
      anchor.href = reservation.applicationFormUrl;
      anchor.download = fallbackFilename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } finally {
      setDownloadingApplicationForm(false);
    }
  }, [reservation, showToast]);

  // 모든 시설물 목록 가져오기 (메모이제이션)
  const fetchAllFacilities = useCallback(async () => {
    try {
      const facilitiesRef = collection(db, 'facilities');
      const facilitiesSnap = await getDocs(facilitiesRef);

      const facilitiesList: Facility[] = facilitiesSnap.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name || '',
          reservationType: data.reservationType || 'start-end',
          availableTimeSlots: data.availableTimeSlots || [],
          operatingHours: data.operatingHours || { start: 0, end: 24 },
          reservationTimeUnit: data.reservationTimeUnit || 1,
          price: data.price || 0,
          basePrice: data.basePrice,
          maxPrice: data.maxPrice,
          discounts: data.discounts || []
        };
      });

      setFacilities(facilitiesList);
    } catch (error: unknown) {
      console.error('시설물 목록 조회 실패:', error);
      const errorMsg = error instanceof Error ? error.message : '시설물 목록 조회에 실패했습니다.';
      showToast(errorMsg, { type: 'error' });
    }
  }, [showToast]);

  // 시설물 정보 가져오기 (메모이제이션)
  const fetchFacilityInfo = useCallback(async (facilityId: string) => {
    try {
      const facilityRef = doc(db, 'facilities', facilityId);
      const facilitySnap = await getDoc(facilityRef);

      if (facilitySnap.exists()) {
        const data = facilitySnap.data();
        const facilityData: Facility = {
          id: facilitySnap.id,
          name: data.name || '',
          reservationType: data.reservationType || 'start-end',
          availableTimeSlots: data.availableTimeSlots || [],
          operatingHours: data.operatingHours || { start: 0, end: 24 },
          reservationTimeUnit: data.reservationTimeUnit || 1,
          price: data.price || 0,
          basePrice: data.basePrice,
          maxPrice: data.maxPrice,
          discounts: data.discounts || []
        };

        setFacility(facilityData);
        return facilityData;
      } else {
        showToast('시설물 정보를 찾을 수 없습니다.', { type: 'warning' });
        return null;
      }
    } catch (error: unknown) {
      console.error('시설물 정보 조회 실패:', error);
      const errorMsg = error instanceof Error ? error.message : '시설물 정보 조회에 실패했습니다.';
      showToast(errorMsg, { type: 'error' });
      return null;
    }
  }, [showToast]);

  useEffect(() => {
    if (!authLoading) {
      // 최고관리자이거나 시설물 예약 관리 권한이 있는 경우에만 접근 가능
      if (!user || (userLevel !== 1 && !permissions?.facilityReservations)) {
        showToast('접근 권한이 없습니다.', { type: 'error' });
        navigate('/admin/dashboard');
        return;
      }

      fetchReservation();
      fetchAllFacilities(); // 시설물 목록 가져오기
    }
  }, [authLoading, user, userLevel, permissions, navigate, fetchReservation, fetchAllFacilities, showToast]);

  // fetchFacilityInfo 의존성 문제 해결
  useEffect(() => {
    if (reservation?.facilityId && fetchFacilityInfo) {
      fetchFacilityInfo(reservation.facilityId);
    }
  }, [reservation?.facilityId, fetchFacilityInfo]);

  // 기존 예약 목록 가져오기 (메모이제이션)
  const fetchExistingReservations = useCallback(async (
    facilityId: string,
    date: Date,
    excludeReservationId?: string
  ) => {
    try {
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);

      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);

      // BloomFilter 에러를 방지하기 위해 분리된 쿼리 사용
      const pendingQuery = query(
        collection(db, 'reservations'),
        where('facilityId', '==', facilityId),
        where('reservationDate', '>=', startOfDay),
        where('reservationDate', '<=', endOfDay),
        where('status', '==', 'pending')
      );

      const approvedQuery = query(
        collection(db, 'reservations'),
        where('facilityId', '==', facilityId),
        where('reservationDate', '>=', startOfDay),
        where('reservationDate', '<=', endOfDay),
        where('status', '==', 'approved')
      );

      // 두 쿼리를 병렬로 실행
      const [pendingSnapshot, approvedSnapshot] = await Promise.all([
        getDocs(pendingQuery),
        getDocs(approvedQuery)
      ]);

      // 결과 합치기
      const allDocs = [...pendingSnapshot.docs, ...approvedSnapshot.docs];

      const reservations: ExistingReservation[] = allDocs
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            startTime: data.startTime || 0,
            endTime: data.endTime || 0
          };
        })
        .filter(res => res.id !== excludeReservationId);

      setExistingReservations(reservations);
      return reservations;
    } catch (error: unknown) {
      console.error('기존 예약 조회 실패:', error);
      const errorMsg = error instanceof Error ? error.message : '기존 예약 조회에 실패했습니다.';
      showToast(errorMsg, { type: 'error' });
      return [];
    }
  }, [showToast]);

  // 시간 겹침 검사 (메모이제이션)
  const isTimeOverlapping = useCallback((startTime: number, endTime: number): boolean => {
    return existingReservations.some(res => {
      return (startTime < res.endTime && endTime > res.startTime);
    });
  }, [existingReservations]);

  // 운영시간 내인지 검사 (메모이제이션)
  const isWithinOperatingHours = useCallback((startTime: number, endTime: number): boolean => {
    if (!facility?.operatingHours) return true;
    return startTime >= facility.operatingHours.start && endTime <= facility.operatingHours.end;
  }, [facility?.operatingHours]);

  // 시간대형인 경우 유효한 시간대인지 확인 (메모이제이션)
  const isValidTimeSlot = useCallback((time: number): boolean => {
    if (!facility) return false;

    if (facility.reservationType === 'time-slot') {
      if (facility.availableTimeSlots && facility.availableTimeSlots.length > 0) {
        return facility.availableTimeSlots.includes(time);
      }

      // 시간대형이지만 availableTimeSlots가 없는 경우, 운영시간 내의 모든 시간대를 사용 가능한 것으로 간주
      const { start, end } = facility.operatingHours || { start: 0, end: 24 };
      return time >= start && time < end;
    }

    return true;
  }, [facility]);

  const handleStatusUpdate = useCallback(async (newStatus: 'approved' | 'rejected') => {
    if (!reservation || !id) return;

    // 감면신청된 예약을 승인하는 경우 확인 팝업
    if (newStatus === 'approved' && reservation.userLevel === 9 && reservation.discountType) {
      const confirmed = window.confirm('사용자가 직접 감면신청한 건입니다. 정말로 승인하시겠습니까?');
      if (!confirmed) {
        return;
      }
    }

    setUpdating(true);
    setError(null);

    try {
      const docRef = doc(db, 'reservations', id);

      await updateDoc(docRef, {
        status: newStatus,
        updatedAt: new Date()
      });

      // 감면신청 승인 시 사용자 레벨 업데이트
      if (newStatus === 'approved' && reservation.userLevel === 9 && reservation.discountType) {
        try {
          const userRef = doc(db, 'user_profiles', reservation.uid);
          const newUserLevel = parseInt(reservation.discountType);

          await updateDoc(userRef, {
            userLevel: newUserLevel,
            updatedAt: new Date().toISOString()
          });

          console.log(`사용자 ${reservation.uid}의 userLevel을 ${newUserLevel}로 업데이트했습니다.`);
          showToast('사용자 등급이 성공적으로 업데이트되었습니다.', { type: 'success' });
        } catch (userUpdateError) {
          console.error('사용자 레벨 업데이트 실패:', userUpdateError);
          showToast('예약은 승인되었지만 사용자 등급 업데이트 중 오류가 발생했습니다.', { type: 'warning' });
        }
      }

      // 승인/거절 시 SMS 발송
      if (newStatus === 'approved' || newStatus === 'rejected') {
        const phoneNumber = reservation.phoneNumber || reservation.phone;
        if (phoneNumber) {
          try {
            const reservationDate = reservation.reservationDate.toLocaleDateString('ko-KR', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            }).replace(/\. /g, '.').replace(/\.$/, '');

            const statusText = newStatus === 'approved' ? '승인' : '거절';
            const smsMessage = `[신항시설관리센터] ${reservation.name} 님 ${reservationDate} ${reservation.facilityName} 예약이 ${statusText}되었습니다.`;

            await SmsService.sendSMS(phoneNumber, smsMessage);
            console.log(`예약 ${statusText} SMS 발송 완료:`, phoneNumber);
            showToast(`SMS 알림이 발송되었습니다.`, { type: 'info' });
          } catch (smsError) {
            console.error('SMS 발송 실패:', smsError);
            const statusText = newStatus === 'approved' ? '승인' : '거절';
            showToast(`예약은 ${statusText}되었으나 SMS 발송에 실패했습니다.`, { type: 'warning' });
          }
        }
      }

      // 업데이트 성공 후 state 업데이트
      setReservation(prev => {
        if (!prev) return null;
        return {
          ...prev,
          status: newStatus,
          updatedAt: new Date()
        };
      });

      const statusText = newStatus === 'approved' ? '승인' : '거절';
      showToast(`예약이 성공적으로 ${statusText}되었습니다.`, { type: 'success' });

    } catch (error: unknown) {
      console.error('상태 업데이트 실패:', error);
      const errorMsg = error instanceof Error ? error.message : '예약 상태 업데이트 중 오류가 발생했습니다.';
      setError(errorMsg);
      showToast(errorMsg, { type: 'error' });
    } finally {
      setUpdating(false);
    }
  }, [reservation, id, showToast]);

  // 사용 가능한 시간대 생성 함수 (메모이제이션)
  const generateAvailableTimes = useCallback((date: Date): number[] => {
    const currentFacility = selectedFacility || facility;
    if (!currentFacility) return [];

    const times: number[] = [];
    const { start, end } = currentFacility.operatingHours || { start: 9, end: 18 };

    // reservationTimeUnit에 따라 시간 간격 조정
    for (let hour = start; hour < end; hour += currentFacility.reservationTimeUnit) {
      // 이미 예약된 시간대가 아닌 경우에만 추가
      if (!isTimeOverlapping(hour, hour + currentFacility.reservationTimeUnit)) {
        times.push(hour);
      }
    }

    return times;
  }, [selectedFacility, facility, isTimeOverlapping]);

  // 수정 모달 열기 (메모이제이션)
  const handleEditClick = useCallback(async (filesOnly = false) => {
    if (!reservation || (!filesOnly && !facility)) return;
    setAttachmentOnly(filesOnly);
    setAttachmentChanges(emptyAttachmentChanges());
    setEditError(null);
    setSubmitStatus('');

    // 현재 시설물 ID 설정
    setSelectedFacilityId(reservation.facilityId);
    setSelectedFacility(facility);

    // 기존 예약 목록 가져오기
    if (!filesOnly) await fetchExistingReservations(reservation.facilityId, reservation.reservationDate, reservation.id);

    // 수정 폼 초기화 - 관리자 생성 예약인 경우 예약자 정보도 포함
    const isManualReservation = isAdminCreated(reservation.uid);
    setEditForm({
      reservationDate: format(reservation.reservationDate, 'yyyy-MM-dd'),
      startTime: reservation.startTime,
      endTime: reservation.endTime,
      name: isManualReservation ? reservation.name : '',
      email: isManualReservation ? reservation.email : '',
      phone: isManualReservation ? (reservation.phoneNumber || reservation.phone || '') : '',
      organization: isManualReservation ? (reservation.organization || '') : ''
    });

    setIsEditModalOpen(true);
  }, [reservation, facility, fetchExistingReservations, isAdminCreated]);

  // 시설물 변경 핸들러
  const handleFacilityChange = useCallback(async (newFacilityId: string) => {
    setSelectedFacilityId(newFacilityId);

    // 선택된 시설물 정보 가져오기
    const newFacility = facilities.find(f => f.id === newFacilityId);
    if (newFacility) {
      setSelectedFacility(newFacility);

      // 시간 초기화 (새 시설물의 운영시간에 맞춤)
      const startTime = newFacility.operatingHours?.start || 0;
      const endTime = startTime + (newFacility.reservationTimeUnit || 1);

      setEditForm(prev => ({
        ...prev,
        startTime,
        endTime
      }));

      // 새로운 시설물의 기존 예약 목록 가져오기
      if (editForm.reservationDate && reservation) {
        await fetchExistingReservations(newFacilityId, new Date(editForm.reservationDate), reservation.id);
      }
    }
  }, [facilities, editForm.reservationDate, reservation, fetchExistingReservations]);

  // 날짜 변경 시 예약 목록 업데이트
  const handleDateChange = useCallback(async (newDate: string) => {
    setEditForm(prev => ({ ...prev, reservationDate: newDate }));

    if (selectedFacilityId && reservation) {
      // 새로운 날짜의 예약 목록 가져오기
      await fetchExistingReservations(selectedFacilityId, new Date(newDate), reservation.id);
    }
  }, [selectedFacilityId, reservation, fetchExistingReservations]);

  // 수정 모달 닫기 (메모이제이션)
  const handleEditClose = useCallback(() => {
    if (editLoading) return;
    setAttachmentChanges(emptyAttachmentChanges());
    setIsEditModalOpen(false);
    setEditError(null);
  }, [editLoading]);

  // 예약 취소 모달 열기
  const handleCancelClick = useCallback(() => {
    setIsCancelModalOpen(true);
    setCancelReason('');
  }, []);

  // 예약 취소 모달 닫기
  const handleCancelClose = useCallback(() => {
    setIsCancelModalOpen(false);
    setCancelReason('');
  }, []);

  // 예약 취소 처리
  const handleCancelSubmit = useCallback(async () => {
    if (!reservation || !id) return;

    if (!cancelReason.trim()) {
      showToast('취소 사유를 입력해주세요.', { type: 'warning' });
      return;
    }

    setUpdating(true);
    try {
      const docRef = doc(db, 'reservations', id);

      await updateDoc(docRef, {
        status: 'cancelled',
        adminComment: `[관리자 취소] ${cancelReason}`,
        updatedAt: new Date()
      });

      // 취소 SMS 발송 (두 건으로 분리)
      const phoneNumber = reservation.phoneNumber || reservation.phone;
      if (phoneNumber) {
        try {
          const reservationDate = reservation.reservationDate.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }).replace(/\. /g, '.').replace(/\.$/, '');

          // 첫 번째 메시지: 예약 취소 안내
          const smsMessage1 = `[신항시설관리센터] ${reservation.name} 님 ${reservationDate} ${reservation.facilityName} 예약이 취소되었습니다.`;

          // 두 번째 메시지: 취소 사유
          const smsMessage2 = `취소 사유: ${cancelReason}`;

          // 첫 번째 SMS 발송
          await SmsService.sendSMS(phoneNumber, smsMessage1);
          console.log('예약 취소 SMS 1차 발송 완료:', phoneNumber);

          // 두 번째 SMS 발송
          await SmsService.sendSMS(phoneNumber, smsMessage2);
          console.log('예약 취소 SMS 2차 발송 완료:', phoneNumber);
        } catch (smsError) {
          console.error('SMS 발송 실패:', smsError);
          showToast('예약은 취소되었으나 SMS 발송에 실패했습니다.', { type: 'warning' });
        }
      }

      // 상태 업데이트
      setReservation(prev => {
        if (!prev) return null;
        return {
          ...prev,
          status: 'cancelled',
          adminComment: `[관리자 취소] ${cancelReason}`,
          updatedAt: new Date()
        };
      });

      setIsCancelModalOpen(false);
      showToast('예약이 성공적으로 취소되었습니다.', { type: 'success' });
    } catch (error: unknown) {
      console.error('예약 취소 실패:', error);
      const errorMsg = error instanceof Error ? error.message : '예약 취소 중 오류가 발생했습니다.';
      showToast(errorMsg, { type: 'error' });
    } finally {
      setUpdating(false);
    }
  }, [reservation, id, cancelReason, showToast]);

  // 예약 수정 처리 (메모이제이션)
  const handleEditSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reservation || !id || editLoading) return;

    const scheduleChanged = !attachmentOnly && (
      selectedFacilityId !== reservation.facilityId ||
      editForm.reservationDate !== format(reservation.reservationDate, 'yyyy-MM-dd') ||
      editForm.startTime !== reservation.startTime || editForm.endTime !== reservation.endTime
    );
    const nameChanged = !attachmentOnly && isAdminCreated(reservation.uid) && (
      editForm.name !== reservation.name || editForm.email !== reservation.email ||
      (editForm.phone || '') !== (reservation.phoneNumber || reservation.phone || '') ||
      (editForm.organization || '') !== (reservation.organization || '')
    );
    if (!scheduleChanged) {
      setEditLoading(true);
      setEditError(null);
      try {
        if (nameChanged && (!editForm.name?.trim() || !editForm.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.email))) {
          throw new Error('이름과 올바른 이메일을 입력해주세요.');
        }
        const data = { updatedAt: new Date(), ...(nameChanged ? {
          name: editForm.name, email: editForm.email, phone: editForm.phone || '',
          phoneNumber: editForm.phone || '', organization: editForm.organization || ''
        } : {}) };
        const result = await saveReservationWithAttachments(doc(db, 'reservations', id), reservation.uid, reservation, attachmentChanges, data, setSubmitStatus);
        const next = { ...reservation, ...data, ...result.attachments } as Reservation;
        setReservation(next);
        setAttachmentUrls(next.fileUrl ? [next.fileUrl] : []);
        setIsEditModalOpen(false);
        showToast('예약이 성공적으로 수정되었습니다.', { type: 'success' });
        if (result.cleanupFailed) showToast('예약은 저장되었으나 일부 이전 파일 삭제에 실패했습니다.', { type: 'warning' });
      } catch (error) {
        setEditError(error instanceof Error ? error.message : '첨부 저장에 실패했습니다.');
      } finally { setEditLoading(false); setSubmitStatus(''); }
      return;
    }

    // 선택된 시설물 정보 확인 (시설물이 변경된 경우 selectedFacility 사용)
    const targetFacility = selectedFacility || facility;
    if (!targetFacility) {
      setEditError('시설물 정보를 찾을 수 없습니다.');
      showToast('시설물 정보를 찾을 수 없습니다.', { type: 'error' });
      return;
    }

    try {
      setEditLoading(true);
      setEditError(null);

      // 관리자 생성 예약인 경우 예약자 정보 유효성 검사
      const isManualReservation = isAdminCreated(reservation.uid);
      if (isManualReservation) {
        if (!editForm.name || !editForm.email) {
          setEditError('이름과 이메일은 필수 항목입니다.');
          showToast('이름과 이메일은 필수 항목입니다.', { type: 'warning' });
          return;
        }

        // 이메일 형식 검증
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(editForm.email)) {
          setEditError('올바른 이메일 형식이 아닙니다.');
          showToast('올바른 이메일 형식이 아닙니다.', { type: 'warning' });
          return;
        }
      }

      const selectedDate = new Date(editForm.reservationDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // 과거 날짜 체크
      if (isBefore(selectedDate, today)) {
        setEditError('과거 날짜는 선택할 수 없습니다.');
        showToast('과거 날짜는 선택할 수 없습니다.', { type: 'warning' });
        return;
      }

      // 종료 시간이 시작 시간보다 이후인지 확인
      if (editForm.endTime <= editForm.startTime) {
        setEditError('종료 시간은 시작 시간보다 이후여야 합니다.');
        showToast('종료 시간은 시작 시간보다 이후여야 합니다.', { type: 'warning' });
        return;
      }

      // 운영시간 체크
      const operatingHours = targetFacility.operatingHours || { start: 0, end: 24 };
      if (editForm.startTime < operatingHours.start || editForm.endTime > operatingHours.end) {
        const errorMsg = `운영시간(${operatingHours.start}시 ~ ${operatingHours.end}시) 내에 선택해주세요.`;
        setEditError(errorMsg);
        showToast(errorMsg, { type: 'warning' });
        return;
      }

      // 시간대형인 경우 선택 가능한 시간대인지 확인
      if (targetFacility.reservationType === 'time-slot') {
        const validSlots = targetFacility.availableTimeSlots || [];
        if (validSlots.length > 0 && !validSlots.includes(editForm.startTime)) {
          setEditError('선택 가능한 시간대만 선택해주세요.');
          showToast('선택 가능한 시간대만 선택해주세요.', { type: 'warning' });
          return;
        }

        // 시간대형의 경우 endTime 재계산
        const calculatedEndTime = editForm.startTime + targetFacility.reservationTimeUnit;
        if (editForm.endTime !== calculatedEndTime) {
          setEditForm(prev => ({ ...prev, endTime: calculatedEndTime }));
        }
      }

      // 선택된 날짜와 시설물의 기존 예약 목록 가져오기
      const targetFacilityId = selectedFacilityId || reservation.facilityId;
      const currentDateReservations = await fetchExistingReservations(targetFacilityId, selectedDate, id);

      // 실제 사용될 시간으로 겹침 검사 (시간대형의 경우 재계산된 endTime 사용)
      const actualEndTime = targetFacility.reservationType === 'time-slot'
        ? editForm.startTime + targetFacility.reservationTimeUnit
        : editForm.endTime;

      const hasTimeConflict = currentDateReservations.some(res => {
        return (editForm.startTime < res.endTime && actualEndTime > res.startTime);
      });

      if (hasTimeConflict) {
        setEditError('선택한 시간에 이미 다른 예약이 있습니다.');
        showToast('선택한 시간에 이미 다른 예약이 있습니다.', { type: 'warning' });
        return;
      }

      // 사용자 레벨 가져오기
      const userRef = doc(db, 'user_profiles', reservation.uid);
      const userSnap = await getDoc(userRef);
      const userLevel = userSnap.exists() ? (Number(userSnap.data().userLevel) || 9) : 9; // 기본값: 일반 사용자

      // 예약 금액 계산 (변경된 시설물 기준, 부가세 포함)
      const newPriceBreakdown = calculateFinalPriceWithVAT(
        {
          price: targetFacility.price,
          basePrice: targetFacility.basePrice,
          maxPrice: targetFacility.maxPrice,
          discounts: targetFacility.discounts
        },
        {
          reservationType: targetFacility.reservationType,
          startTime: editForm.startTime,
          endTime: actualEndTime,
          reservationTimeUnit: targetFacility.reservationTimeUnit
        },
        userLevel
      );
      const newPrice = newPriceBreakdown.total;

      // 예약 정보 업데이트
      const reservationRef = doc(db, 'reservations', id);
      const updateData: any = {
        reservationDate: selectedDate,
        startTime: editForm.startTime,
        endTime: actualEndTime, // 실제 계산된 종료 시간 사용
        price: newPrice,
        userLevel: isManualReservation ? null : userLevel,
        updatedAt: new Date()
      };

      // 시설물이 변경된 경우 시설물 정보도 업데이트
      if (selectedFacilityId && selectedFacilityId !== reservation.facilityId) {
        updateData.facilityId = selectedFacilityId;
        updateData.facilityName = targetFacility.name;
        updateData.reservationType = targetFacility.reservationType;
      }

      // 관리자 생성 예약인 경우 예약자 정보도 업데이트
      if (isManualReservation) {
        updateData.name = editForm.name;
        updateData.email = editForm.email;
        updateData.phone = editForm.phone || '';
        updateData.phoneNumber = editForm.phone || '';
        updateData.organization = editForm.organization || '';
      }

      const attachmentResult = await saveReservationWithAttachments(reservationRef, reservation.uid, reservation, attachmentChanges, updateData, setSubmitStatus);
      if (attachmentResult.cleanupFailed) showToast('예약은 저장되었으나 일부 이전 파일 삭제에 실패했습니다.', { type: 'warning' });
      const finalFileUrl = attachmentResult.attachments.fileUrl === undefined ? reservation.fileUrl : attachmentResult.attachments.fileUrl;
      setAttachmentUrls(finalFileUrl ? [finalFileUrl] : []);

      // 상태 업데이트
      setReservation(prev => {
        if (!prev) return null;
        return {
          ...prev,
          ...attachmentResult.attachments,
          reservationDate: selectedDate,
          startTime: editForm.startTime,
          endTime: actualEndTime, // 실제 계산된 종료 시간 사용
          price: newPrice,
          userLevel: isManualReservation ? undefined : userLevel,
          updatedAt: new Date(),
          // 시설물이 변경된 경우 업데이트
          ...(selectedFacilityId && selectedFacilityId !== reservation.facilityId ? {
            facilityId: selectedFacilityId,
            facilityName: targetFacility.name,
            reservationType: targetFacility.reservationType
          } : {}),
          // 관리자 생성 예약인 경우 예약자 정보 업데이트
          ...(isManualReservation ? {
            name: editForm.name || prev.name,
            email: editForm.email || prev.email,
            phone: editForm.phone || prev.phone,
            phoneNumber: editForm.phone || prev.phoneNumber,
            organization: editForm.organization || prev.organization
          } : {})
        };
      });

      // 시설물이 변경된 경우 시설물 정보도 업데이트
      if (selectedFacilityId && selectedFacilityId !== reservation.facilityId) {
        setFacility(targetFacility);
      }

      setIsEditModalOpen(false);
      showToast('예약이 성공적으로 수정되었습니다.', { type: 'success' });
    } catch (err: unknown) {
      console.error('예약 수정 중 오류가 발생했습니다:', err);
      const errorMsg = err instanceof Error ? err.message : '예약 수정 중 오류가 발생했습니다.';
      setEditError(errorMsg);
      showToast(errorMsg, { type: 'error' });
    } finally {
      setEditLoading(false);
      setSubmitStatus('');
    }
  }, [
    reservation,
    attachmentOnly,
    attachmentChanges,
    editLoading,
    facility,
    selectedFacility,
    selectedFacilityId,
    id,
    editForm,
    showToast,
    fetchExistingReservations,
    isAdminCreated
  ]);

  if (loading) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#155acb]"></div>
        </div>
      </div>
    );
  }

  if (error || !reservation) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="max-w-[1200px] mx-auto relative pt-8 px-4">
          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <div className="p-8">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-8 w-8 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-red-800 font-['Pretendard']">오류 발생</h3>
                  <div className="mt-2 text-sm text-red-700 font-['Pretendard']">
                    <p>{error || '예약 정보를 불러올 수 없습니다.'}</p>
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/admin/facility-reservations')}
                  className="bg-[#155acb] text-white px-6 py-3 rounded-sm hover:bg-[#155acb]/90 font-medium font-['Pretendard'] transition-colors"
                >
                  예약 목록으로 돌아가기
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-[#f7f9fd]">
      {/* 본문 컨테이너 - 중앙 정렬 */}
      <div className="max-w-[1200px] mx-auto relative pt-8 px-4">
        {/* 헤더 섹션 */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
              시설물 예약 상세
            </div>
            <div className="flex space-x-2">
              {reservation.status === 'approved' && (
                <button
                  onClick={handleCancelClick}
                  className="bg-red-600 text-white px-6 py-3 rounded-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 font-medium font-['Pretendard'] transition-colors"
                >
                  예약 취소
                </button>
              )}
              {(reservation.status === 'pending' || reservation.status === 'approved') && (
                <button
                  onClick={() => handleEditClick(false)}
                  disabled={!facility}
                  className="bg-yellow-600 text-white px-6 py-3 rounded-sm hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 font-medium font-['Pretendard'] transition-colors"
                >
                  예약 수정
                </button>
              )}
              <button onClick={() => handleEditClick(true)} className="bg-yellow-600 text-white px-6 py-3 rounded-sm hover:bg-yellow-700">첨부 관리</button>
              <button
                onClick={() => navigate('/admin/facility-reservations')}
                className="bg-[#6b7684] text-white px-6 py-3 rounded-sm hover:bg-[#6b7684]/90 font-medium font-['Pretendard'] transition-colors"
              >
                목록으로 돌아가기
              </button>
            </div>
          </div>
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
        </div>

        {/* 예약 상태 뱃지 */}
        <div className="mt-8">
          <span className={`px-4 py-2 inline-flex text-sm font-semibold font-['Pretendard'] rounded-full ${STATUS_COLORS[reservation.status]}`}>
            {STATUS_TEXTS[reservation.status]}
          </span>
        </div>

        {/* 예약 정보 카드 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
              예약 정보
            </h2>
            <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>
            
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <dt className="text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">시설물</dt>
                <dd className="text-[#6b7684] text-base font-['Pretendard']">{reservation.facilityName}</dd>
              </div>
              <div>
                <dt className="text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">예약 날짜</dt>
                <dd className="text-[#6b7684] text-base font-['Pretendard']">
                  {reservation.reservationDate.toLocaleDateString()}
                </dd>
              </div>
              <div>
                <dt className="text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">예약 시간</dt>
                <dd className="text-[#6b7684] text-base font-['Pretendard']">
                  {reservation.startTime}:00 ~ {reservation.endTime}:00
                </dd>
              </div>
              <div>
                <dt className="text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">예약 유형</dt>
                <dd className="text-[#6b7684] text-base font-['Pretendard']">
                  {reservation.reservationType === 'time-slot' ? '시간대 예약' : '시작-종료 예약'}
                </dd>
              </div>
              {reservation.price !== undefined && (
                <div className="md:col-span-2">
                  <dt className="text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">예약 금액</dt>
                  <dd className="text-[#6b7684] text-base font-['Pretendard']">
                    {reservation.price.toLocaleString()}원
                    {(() => {
                      if (getDiscountInfo && getOriginalPrice > reservation.price) {
                        return (
                          <div className="mt-2 space-y-1">
                            <div className="text-sm text-[#6b7684] font-['Pretendard']">
                              원래 가격: <span className="line-through">{getOriginalPrice.toLocaleString()}원</span>
                            </div>
                            <div className={`text-sm font-medium font-['Pretendard'] ${
                              getDiscountInfo.type === 'manual' ? 'text-orange-600' : 'text-blue-600'
                            }`}>
                              {getDiscountInfo.description}: {getDiscountInfo.discountRate}% 할인 적용
                              {getDiscountInfo.type === 'manual' && (
                                <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                                  감면신청
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>

        {/* 예약자 정보 카드 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9">
                예약자 정보
              </h2>
              {isAdminCreated(reservation.uid) && (
                <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm font-semibold font-['Pretendard'] rounded-full">
                  관리자 생성
                </span>
              )}
            </div>
            <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>
            
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <dt className="text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">이름</dt>
                <dd className="text-[#6b7684] text-base font-['Pretendard']">{reservation.name}</dd>
              </div>
              <div>
                <dt className="text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">이메일</dt>
                <dd className="text-[#6b7684] text-base font-['Pretendard']">{reservation.email}</dd>
              </div>
              <div>
                <dt className="text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">소속</dt>
                <dd className="text-[#6b7684] text-base font-['Pretendard']">{reservation.organization || '정보 없음'}</dd>
              </div>
              <div>
                <dt className="text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">연락처</dt>
                <dd className="text-[#6b7684] text-base font-['Pretendard']">{reservation.phoneNumber || reservation.phone || '정보 없음'}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* 예약 처리 카드 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
              예약 처리
            </h2>
            <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>
            
            {reservation.status === 'pending' ? (
              <p className="text-[#6b7684] font-['Pretendard']">아래 버튼을 사용하여 예약을 승인하거나 거절할 수 있습니다.</p>
            ) : (
              <p className="text-[#6b7684] font-['Pretendard']">
                이 예약은 이미 
                <span className={`font-medium ${
                  reservation.status === 'approved' ? 'text-green-600' : 
                  reservation.status === 'rejected' ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {' '}{reservation.status === 'approved' ? '승인' : 
                    reservation.status === 'rejected' ? '거절' : 
                    reservation.status === 'cancelled' ? '취소' : '처리'}
                </span>
                되었습니다.
              </p>
            )}
          </div>
        </div>

        {/* 대관신청서 카드 */}
        {reservation.applicationFormUrl && (
          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <div className="p-8">
              <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                대관신청서
              </h2>
              <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>

              <div className="flex items-center justify-between">
                <div className="text-sm text-[#6b7684] font-['Pretendard']">
                  {reservation.applicationFormFileName || '첨부됨'}
                  {reservation.rentalTermsAgreed && (
                    <span className="ml-2 text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded">
                      대관규약 동의 확인
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleDownloadApplicationForm}
                  disabled={downloadingApplicationForm}
                  className="text-sm text-[#155acb] hover:text-[#155acb]/80 font-['Pretendard'] transition-colors disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  {downloadingApplicationForm ? '다운로드 중...' : '다운로드'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 첨부파일 카드 */}
        {attachmentUrls.length > 0 && (
          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <div className="p-8">
              <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                첨부파일
              </h2>
              <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>
              
              <ul className="divide-y divide-[#e5e8eb]">
                {attachmentUrls.map((url, index) => (
                  <li key={index} className="py-3 flex justify-between items-center">
                    <span className="flex items-center text-sm text-[#6b7684] font-['Pretendard']">
                      <svg className="mr-2 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z" clipRule="evenodd" />
                      </svg>
                      {reservation.fileName || '첨부파일'}
                    </span>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[#155acb] hover:text-[#155acb]/80 font-['Pretendard'] transition-colors"
                    >
                      다운로드
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* 승인/거절 버튼 */}
        {reservation.status === 'pending' && (
          <div className="mt-8 flex justify-end space-x-4">
            <button
              onClick={() => handleStatusUpdate('rejected')}
              disabled={updating}
              className="bg-red-600 text-white px-8 py-3 rounded-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium font-['Pretendard'] transition-colors"
            >
              {updating ? '처리 중...' : '예약 거절'}
            </button>
            <button
              onClick={() => handleStatusUpdate('approved')}
              disabled={updating}
              className="bg-green-600 text-white px-8 py-3 rounded-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium font-['Pretendard'] transition-colors"
            >
              {updating ? '처리 중...' : '예약 승인'}
            </button>
          </div>
        )}
        
        {/* 예약 이력 카드 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
              예약 이력
            </h2>
            <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>
            
            <ul className="divide-y divide-[#e5e8eb]">
              <li className="py-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-8 w-8 bg-[#155acb] rounded-full flex items-center justify-center">
                    <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-[#1e2c49] font-['Pretendard']">예약 생성됨</p>
                    <p className="text-sm text-[#6b7684] font-['Pretendard']">{reservation.createdAt.toLocaleString()}</p>
                  </div>
                </div>
              </li>
              
              {reservation.status !== 'pending' && (
                <li className="py-4">
                  <div className="flex items-center">
                    <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${
                      reservation.status === 'approved' ? 'bg-green-500' : 
                      reservation.status === 'rejected' ? 'bg-red-500' : 'bg-gray-500'
                    }`}>
                      {reservation.status === 'approved' ? (
                        <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-[#1e2c49] font-['Pretendard']">
                        {reservation.status === 'approved' ? '예약 승인됨' : 
                        reservation.status === 'rejected' ? '예약 거절됨' : '예약 취소됨'}
                      </p>
                      <p className="text-sm text-[#6b7684] font-['Pretendard']">{reservation.updatedAt.toLocaleString()}</p>
                    </div>
                  </div>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* 수정 모달 */}
        {isEditModalOpen && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-sm p-8 max-w-2xl w-full outline outline-1 outline-slate-200 max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-bold text-[#1e2c49] font-['Pretendard'] mb-6">{attachmentOnly ? '첨부 관리' : '예약 수정'}</h3>
              <form onSubmit={handleEditSubmit}>
                <fieldset disabled={editLoading} className="space-y-4">
                  {!attachmentOnly && <div className="space-y-4">
                  {/* 관리자 생성 예약인 경우에만 예약자 정보 입력 필드 표시 */}
                  {reservation && isAdminCreated(reservation.uid) && (
                    <>
                      <div className="border-b border-[#e5e8eb] pb-4 mb-4">
                        <h4 className="text-sm font-bold text-[#1e2c49] font-['Pretendard'] mb-4">예약자 정보</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">이름 *</label>
                            <input
                              type="text"
                              value={editForm.name}
                              onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                              className="w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">이메일 *</label>
                            <input
                              type="email"
                              value={editForm.email}
                              onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                              className="w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">연락처</label>
                            <input
                              type="tel"
                              value={editForm.phone}
                              onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                              className="w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                            />
                          </div>
                          <div>
                            <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">소속</label>
                            <input
                              type="text"
                              value={editForm.organization}
                              onChange={(e) => setEditForm(prev => ({ ...prev, organization: e.target.value }))}
                              className="w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">시설물 선택</label>
                    <select
                      value={selectedFacilityId}
                      onChange={(e) => handleFacilityChange(e.target.value)}
                      className="w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                    >
                      {facilities.map(fac => (
                        <option key={fac.id} value={fac.id}>
                          {fac.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">예약 날짜</label>
                    <input
                      type="date"
                      value={editForm.reservationDate}
                      onChange={(e) => handleDateChange(e.target.value)}
                      className="w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                      min={editForm.reservationDate === format(reservation.reservationDate, 'yyyy-MM-dd') ? undefined : format(new Date(), 'yyyy-MM-dd')}
                    />
                  </div>

                  {(selectedFacility || facility)?.reservationType === 'time-slot' ? (
                    <div>
                      <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">이용 시간</label>
                      <select
                        value={editForm.startTime}
                        onChange={(e) => {
                          const time = parseInt(e.target.value);
                          const currentFacility = selectedFacility || facility;
                          setEditForm(prev => ({
                            ...prev,
                            startTime: time,
                            endTime: time + (currentFacility?.reservationTimeUnit || 1)
                          }));
                        }}
                        className="w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                      >
                        {generateAvailableTimes(new Date(editForm.reservationDate)).map(time => {
                          const currentFacility = selectedFacility || facility;
                          return (
                            <option key={time} value={time}>
                              {String(time).padStart(2, '0')}:00 ~ {String(time + (currentFacility?.reservationTimeUnit || 1)).padStart(2, '0')}:00
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">시작 시간</label>
                        <select
                          value={editForm.startTime}
                          onChange={(e) => {
                            const newStartTime = parseInt(e.target.value);
                            setEditForm(prev => ({
                              ...prev,
                              startTime: newStartTime,
                              // 종료 시간이 시작 시간보다 이전이거나 같으면 자동으로 조정
                              endTime: prev.endTime <= newStartTime ? newStartTime + 1 : prev.endTime
                            }));
                          }}
                          className="w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                        >
                          {Array.from(
                            { length: ((selectedFacility || facility)?.operatingHours?.end || 24) - ((selectedFacility || facility)?.operatingHours?.start || 0) },
                            (_, i) => i + ((selectedFacility || facility)?.operatingHours?.start || 0)
                          ).map(hour => (
                            <option key={hour} value={hour}>
                              {String(hour).padStart(2, '0')}:00
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">종료 시간</label>
                        <select
                          value={editForm.endTime}
                          onChange={(e) => setEditForm(prev => ({ ...prev, endTime: parseInt(e.target.value) }))}
                          className="w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                        >
                          {Array.from(
                            { length: ((selectedFacility || facility)?.operatingHours?.end || 24) - editForm.startTime },
                            (_, i) => i + editForm.startTime + 1
                          ).map(hour => (
                            <option key={hour} value={hour}>
                              {String(hour).padStart(2, '0')}:00
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                </div>}
                  <ReservationAttachmentFields existing={reservation} changes={attachmentChanges} onChange={setAttachmentChanges} disabled={editLoading} facilityName={(selectedFacility || facility)?.name || reservation.facilityName} />
                </fieldset>
                {submitStatus && <p role="status" className="mt-4 text-sm text-blue-700">{submitStatus}</p>}
                {editError && (
                  <p className="mt-4 text-sm text-red-600 font-['Pretendard']">{editError}</p>
                )}

                <div className="mt-8 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleEditClose}
                    disabled={editLoading}
                    className="px-6 py-3 text-sm font-medium text-[#6b7684] bg-white border border-[#e5e8eb] rounded-sm hover:bg-[#f8fbff] font-['Pretendard'] transition-colors"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={editLoading}
                    className="px-6 py-3 text-sm font-medium text-white bg-[#155acb] rounded-sm hover:bg-[#155acb]/90 disabled:opacity-50 font-['Pretendard'] transition-colors"
                  >
                    {editLoading ? '수정 중...' : '수정하기'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 취소 모달 */}
        {isCancelModalOpen && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-sm p-8 max-w-md w-full outline outline-1 outline-slate-200">
              <h3 className="text-lg font-bold text-[#1e2c49] font-['Pretendard'] mb-6">예약 취소</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">
                    취소 사유 *
                  </label>
                  <textarea
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    className="w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard'] min-h-[120px]"
                    placeholder="예약 취소 사유를 입력해주세요."
                    required
                  />
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-4">
                  <p className="text-sm text-yellow-800 font-['Pretendard']">
                    예약을 취소하면 예약자에게 SMS로 취소 알림이 발송됩니다.
                  </p>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCancelClose}
                    disabled={updating}
                    className="px-6 py-3 text-sm font-medium text-[#6b7684] bg-white border border-[#e5e8eb] rounded-sm hover:bg-[#f8fbff] font-['Pretendard'] transition-colors disabled:opacity-50"
                  >
                    닫기
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelSubmit}
                    disabled={updating}
                    className="px-6 py-3 text-sm font-medium text-white bg-red-600 rounded-sm hover:bg-red-700 disabled:opacity-50 font-['Pretendard'] transition-colors"
                  >
                    {updating ? '처리 중...' : '예약 취소'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 하단 여백 */}
        <div className="h-16"></div>
      </div>
    </div>
  );
};

export default FacilityReservationDetailPage; 
